<template>
  <UiModal
    v-if="order"
    :is-shown="isShown"
    :teleport-target="teleportTarget"
    :title="t('title')"
    size="s"
    modal-class="review-collection-popup"
    is-rounded
    is-footer-hidden
    is-close-button-filled
    is-closed-by-click-outside
    @close="onClose"
  >
    <template #content>
      <ReviewCollectionCard
        :order="order"
        :disabled="isSubmitting"
        horizontal
        @rate="onRate"
        @order-click="onOrderClick"
      />
    </template>
  </UiModal>
</template>

<script setup lang="ts">
import { UiModal } from '@experience/core-modal-ui';
import { useI18n } from '@experience/i18n';
import { computed, onMounted, ref, watch } from 'vue';

import { buildReviewOrderEventParams } from '@/entities/pending-review/lib/reviewAnalyticsParams';
import type { PendingReviewOrder } from '@/entities/pending-review/model/types';
import { useAnalytics } from '@/shared/lib/analytics';
import { initModalTeleportTarget } from '@/shared/lib/initModalTeleportTarget';

import ru from './i18n/ru';
import ReviewCollectionCard from './ui/ReviewCollectionCard.vue';

const props = defineProps<{
  order: PendingReviewOrder | null;
  isShown: boolean;
  isSubmitting?: boolean;
}>();

const emit = defineEmits<{
  close: [];
  rate: [value: number];
}>();

const modalTarget = ref<HTMLElement | null>(null);

const { trackEvent } = useAnalytics();

type MessageSchema = typeof ru;
const { t } = useI18n<{ message: MessageSchema }, 'ru'>({
  inheritLocale: true,
  messages: { ru },
});

const teleportTarget = computed(() => modalTarget.value || undefined);

onMounted(() => {
  initModalTeleportTarget(modalTarget);
});

watch(
  () => props.isShown,
  (isShown) => {
    if (isShown && props.order) {
      void trackEvent('main:review-pop-up.show', buildReviewOrderEventParams(props.order));
    }
  },
  { immediate: true },
);

const onRate = (value: number) => {
  if (props.order) {
    void trackEvent('main:review-pop-up:rate.select', {
      ...buildReviewOrderEventParams(props.order),
      review_rate: value,
    });
  }

  emit('rate', value);
};

const onOrderClick = () => {
  if (props.order) {
    void trackEvent('main:review-pop-up:order.click', buildReviewOrderEventParams(props.order));
  }
};

const onClose = () => {
  if (props.order) {
    void trackEvent('main:review-pop-up.close', buildReviewOrderEventParams(props.order));
  }

  emit('close');
};
</script>

<style lang="scss">
.review-collection-popup.ui-modal {
  .ui-modal__body-content-wrapper {
    padding: var(--alias-space-8) var(--alias-space-24) var(--alias-space-24);

    @include adaptivity.mobile {
      padding: 0 var(--alias-space-16) var(--alias-space-24);
    }
  }

  .ui-modal-header__title {
    // на ноутбуке и шире укрупняем заголовок (design-tokens-2 отдаёт только
    // max-width миксины, поэтому min-width пишем напрямую — эквивалент mf-laptop)
    @media (width >= 1024px) {
      @include typography.base-700-700-semi;
    }
  }
}
</style>
